import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  Link,
  DialogActions,
} from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { updateConsent } from '../api'
import Navbar from './Navbar';
import ForgotPassword from '../components/ForgotPassword';

const TERMS_VERSION = process.env.TERMS_VERSION;

export default function Login() {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const [consentOpen, setConsentOpen] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [consentVersion, setConsentVersion] = useState(null);


  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const user = await login(formData.email.toLowerCase(), formData.password);
      
      const needsConsent = user?.required;

      console.log("consent:", needsConsent)

      if (needsConsent) {
        setPendingUser(user);
        setConsentVersion(user?.currentVersion || TERMS_VERSION);
        setConsentOpen(true);
        return;
      }
      
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Accept updated terms from dialog
  const acceptConsent = async () => {
    if (!pendingUser) return;
    setAccepting(true);
    try {
      await updateConsent(pendingUser.id);
      setConsentOpen(false);
      // proceed to app
      if (pendingUser.role === 'admin') navigate('/admin');
      else navigate('/dashboard');
    } catch (e) {
      setError(e.message);
    } finally {
      setAccepting(false);
    }
  };

  // Optional: decline updated terms — sign out or just close
  // const declineConsent = () => {
  //   setConsentOpen(false);
  //   setPendingUser(null);
  //   // If you stored a token on login already, log out so the app is clean
  //   if (logout) logout();
  // };

  // Show forgot password component if state is true
  if (showForgotPassword) {
    return (
      <Box sx={{ minHeight: '100vh', background: theme.palette.background.gradient }}>
        <Navbar />
        <ForgotPassword onBackToLogin={() => setShowForgotPassword(false)} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: theme.palette.background.gradient }}>
      <Navbar />
      
      <Container maxWidth="sm" sx={{ pt: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper 
            sx={{ 
              p: 4, 
              width: '100%',
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              borderRadius: 3,
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <LoginIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700,
                  mb: 1,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Welcome Back
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Sign in to your Ski Fleet account
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    },
                  },
                }}
              />
              
              <TextField
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    },
                  },
                }}
              />

              {/* Forgot Password Link */}
              <Box sx={{ textAlign: 'right', mt: -1 }}>
                <Button
                  onClick={() => setShowForgotPassword(true)}
                  sx={{ 
                    color: theme.palette.primary.main,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Forgot Password?
                </Button>
              </Box>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
                sx={{
                  py: 1.5,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5b5eea 0%, #7c3aed 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.4)',
                  },
                  '&:disabled': {
                    background: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Don't have an account?{' '}
                <Button
                  onClick={() => navigate('/register')}
                  sx={{ 
                    color: theme.palette.primary.main,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Sign up
                </Button>
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>
      {/* CONSENT POPUP */}
      <Dialog
        open={consentOpen}
        // prevent closing without a decision (optional)
        onClose={() => {}}
        disableEscapeKeyDown
        aria-labelledby="consent-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="consent-title">Updated Terms & Privacy</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            We’ve updated our Terms & Conditions and Privacy Policy (version {consentVersion || TERMS_VERSION}). 
            Please review and accept to continue.
          </Typography>
          <Typography variant="body2">
            <Link href="/terms" target="_blank" rel="noopener">Read Terms</Link>{' '}·{' '}
            <Link href="/privacy" target="_blank" rel="noopener">Read Privacy</Link>
          </Typography>
        </DialogContent>
        <DialogActions>
          {/* <Button onClick={declineConsent} disabled={accepting}>Decline</Button> */}
          <Button onClick={acceptConsent} variant="contained" disabled={accepting}>
            {accepting ? 'Saving...' : 'I Accept'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 